import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../../services/api.service';

export interface Task {
  id: string;
  title: string;
  description: string;
  badge: string;
  badgeType: 'library' | 'custom';
  timeframe: string;
  urgency: 'immediate' | 'week' | 'month' | 'months';
  category: string;
  timeBucket: 'immediate' | '1week' | '30days' | '90days' | '6_12mo';
  completed?: boolean;
}

export interface TaskGroup {
  title: string;
  subtitle: string;
  urgency: 'immediate' | 'week' | 'month' | 'months';
  category: string;
  tasks: Task[];
}

export interface TimeFilter {
  label: string;
  value: string;
  count: number;
}

export interface CategoryFilter {
  label: string;
  value: string;
  count: number;
}

@Component({
  selector: 'app-checklist',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './checklist.html',
  styleUrl: './checklist.css'
})
export class ChecklistComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  loading = false;
  searchQuery = '';
  canAccessChecklist = false;
  selectedTimeFilter = 'all';
  selectedCategory = 'all';
  activeNav = 'home';
  totalTasks = 0;
  checklists: any[] = [];
  filteredChecklists: any[] = [];
  selectedChecklist: any = null;

  // ── Checked task IDs (supports multiple) ─────────────────────
  checkedTaskIds: Set<string> = new Set();

  // ── Detail panel state ────────────────────────────────────────
  selectedTask: Task | null = null;

  // ── Confirmation popup state ──────────────────────────────────
  confirmTask: Task | null = null;

  // ── Urgency → readable label map ─────────────────────────────
  private urgencyLabelMap: Record<string, string> = {
    immediate: 'Immediate Priority',
    week:      'High Priority',
    month:     'Medium Priority',
    months:    'Long-Term Priority',
  };

  // ── Urgency → category label map ─────────────────────────────
  private urgencyCategoryMap: Record<string, string> = {
    immediate: 'Critical First Steps',
    week:      'Legal & Financial Foundations',
    month:     'Personal Affairs',
    months:    'Legal & Financial Cleanup',
  };
  timeFilters: TimeFilter[] = [];


  categoryFilters: CategoryFilter[] = [];

 allTasks: Task[] = [];

  filteredGroups: TaskGroup[] = [];
  currentPage = 1;
itemsPerPage = 10;
totalPages = 1;
pages: number[] = [];

  get totalFilteredTasks(): number {
    return this.filteredGroups.reduce((sum, g) => sum + g.tasks.length, 0);
  }

  constructor(
  private router: Router,
  private apiService: ApiService
) {}
ngOnInit(): void {

  this.canAccessChecklist =
    localStorage.getItem('checklistUnlocked') === 'true';

  const beneficiaryId =
    localStorage.getItem('beneficiaryId');

  if (beneficiaryId && beneficiaryId !== 'null') {
    this.loadChecklist();
  }

}
loadChecklist(): void {
  this.loading = true;

this.apiService.getChecklistDetails(
  this.currentPage,
  this.itemsPerPage
).subscribe({
    next: (res: any) => {

      console.log('API Response:', res);

      // ✅ STEP 1: Handle API error response
      if (!res || res.detail) {
        console.error('API Failed:', res?.detail);
        this.allTasks = [];
        this.totalTasks = 0;

        this.buildDynamicFilters();
        this.buildFilteredGroups();

        this.loading = false;
        return;
      }

      // ✅ STEP 2: Extract correct data safely
      const data = Array.isArray(res?.taskDetails)
        ? res.taskDetails
        : Array.isArray(res?.data)
        ? res.data
        : [];

      // ✅ STEP 3: Map API → UI model
      this.allTasks = data.map((item: any, index: number) => ({
        id: item.task_id || String(index + 1),
        title: item.task_title || 'No Title',
        description: item.notes || '',
        badge: 'Library',
        badgeType: 'library',
        timeframe: this.getTimeframe(item.priority),
        urgency: this.getUrgency(item.priority),
        category: this.getCategory(item.section),
        timeBucket: this.getTimeBucket(item.priority),
        completed: item.is_completed === true
      }));

      // ✅ STEP 4: Update counts & UI
// ✅ STEP 4: Update counts & UI
this.totalTasks = res.total || this.allTasks.length;

this.totalPages = res.pages || 1;

this.pages = Array.from(
  { length: this.totalPages },
  (_, i) => i + 1
);

      this.buildDynamicFilters();   // categories + counts
      this.buildFilteredGroups();   // UI groups

      // ✅ STEP 5: Default selection
      if (this.allTasks.length > 0) {
        this.selectedTask = this.allTasks[0];
      }

      this.loading = false;
    },

    error: (err) => {
      console.error('Checklist API Error:', err);

      this.allTasks = [];
      this.totalTasks = 0;

      this.buildDynamicFilters();
      this.buildFilteredGroups();

      this.loading = false;
    }
  });
}
buildDynamicFilters(): void {

  // ✅ ONLY NOT COMPLETED TASKS
  const activeTasks = this.allTasks.filter(t => !t.completed);

  // ✅ TIME FILTERS
  this.timeFilters = [
    {
      label: 'All',
      value: 'all',
      count: activeTasks.length
    }
  ];

  const timeMap: any = {
    immediate: 'Immediate',
    '1week': '1 Week',
    '30days': '30 Days',
    '90days': '90 Days',
    '6_12mo': '6–12 Mo'
  };

  Object.keys(timeMap).forEach(key => {
    const count = activeTasks.filter(
      t => t.timeBucket === key
    ).length;

    if (count > 0) {
      this.timeFilters.push({
        label: timeMap[key],
        value: key,
        count: count
      });
    }
  });

  // ✅ CATEGORY FILTERS WITH COUNT
  this.categoryFilters = [
    {
      label: 'All Categories',
      value: 'all',
      count: activeTasks.length
    }
  ];

  const categoryMap: Record<string, number> = {};

  activeTasks.forEach(task => {
    const cat = task.category || 'general';

    if (!categoryMap[cat]) {
      categoryMap[cat] = 0;
    }

    categoryMap[cat]++;
  });

  Object.keys(categoryMap).forEach(cat => {
    this.categoryFilters.push({
      label: this.formatCategory(cat),
      value: cat,
      count: categoryMap[cat]
    });
  });
}
formatCategory(value: string): string {

  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());

}
getUrgency(priority: string): any {
  if (priority === 'IMMEDIATE') return 'immediate';
  if (priority === 'HIGH') return 'week';
  if (priority === 'MEDIUM') return 'month';
  return 'months';
}

getTimeBucket(priority: string): any {
  if (priority === 'IMMEDIATE') return 'immediate';
  if (priority === 'HIGH') return '1week';
  if (priority === 'MEDIUM') return '30days';
  return '90days';
}

getTimeframe(priority: string): string {
  if (priority === 'IMMEDIATE') return 'Within 48 Hours';
  if (priority === 'HIGH') return 'Within 1 Week';
  if (priority === 'MEDIUM') return 'Within 30 Days';
  return 'Long Term';
}
getCategory(section: string): string {

  if (!section) return 'general';

  return section.toLowerCase();

}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Checkbox click — opens confirmation popup ─────────────────
 onCheckboxClick(task: Task): void {

  if (!this.canAccessChecklist) {
    return;
  }

  if (this.checkedTaskIds.has(task.id)) {
    return;
  }

  this.confirmTask = task;
}

 
confirmComplete(): void {
  if (!this.confirmTask) return;

  const taskId = this.confirmTask.id;
  this.loading = true;

  this.apiService.completeTask(taskId).subscribe({
    next: () => {

      // ✅ STEP 1: update UI instantly
      this.allTasks = this.allTasks.map(task =>
        task.id === taskId
          ? { ...task, completed: true }
          : task
      );
   

      // ✅ STEP 2: mark checkbox state (if used in HTML)
      this.checkedTaskIds.add(taskId);

      // ✅ STEP 3: rebuild UI groups
      this.buildFilteredGroups();

      // (optional) reload backend
      this.loadChecklist();

      this.confirmTask = null;
      this.loading = false;
    },

    error: (err) => {
      console.error('Complete API Error:', err);
      this.loading = false;
      this.confirmTask = null;
    }
  });
}
  // ── User cancelled ────────────────────────────────────────────
  cancelConfirm(): void {
    this.confirmTask = null;
  }

  // ── Keep original toggleCheck for any other usage ─────────────
  toggleCheck(taskId: string): void {
    if (this.checkedTaskIds.has(taskId)) {
      this.checkedTaskIds.delete(taskId);
    } else {
      this.checkedTaskIds.add(taskId);
    }
  }

  isChecked(taskId: string): boolean {
    return this.checkedTaskIds.has(taskId);
  }

  // ── Search / Filter ───────────────────────────────────────────
onSearch(): void {
  this.currentPage = 1;
  this.buildFilteredGroups();
}
applyFilters(): void {

  const query = this.searchQuery.toLowerCase().trim();

  this.filteredChecklists = this.checklists.filter(item =>
    item.title.toLowerCase().includes(query) ||
    item.description.toLowerCase().includes(query)
  );

}

  selectTimeFilter(value: string): void {

  this.selectedTimeFilter = value;
  this.currentPage = 1;

  this.buildFilteredGroups();
}

 selectCategory(value: string): void {

  this.selectedCategory = value;
  this.currentPage = 1;

  this.buildFilteredGroups();
}
  selectChecklist(item: any): void {
  this.selectedChecklist = item;
}
buildFilteredGroups(): void {

  let tasks = [...this.allTasks];

  // ✅ SEARCH FILTER
  if (this.searchQuery && this.searchQuery.trim()) {

    const q = this.searchQuery.toLowerCase().trim();

    tasks = tasks.filter((t) =>
      (t.title || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q) ||
      (t.category || '').toLowerCase().includes(q) ||
      (t.timeframe || '').toLowerCase().includes(q)
    );
  }

  // ✅ TIME FILTER
  if (this.selectedTimeFilter !== 'all') {

    tasks = tasks.filter(
      (t) => t.timeBucket === this.selectedTimeFilter
    );

  }

  // ✅ CATEGORY FILTER
  if (this.selectedCategory !== 'all') {

    tasks = tasks.filter(
      (t) => t.category === this.selectedCategory
    );

  }

  // ✅ GROUPING
  const groupMap: Record<string, TaskGroup> = {

    immediate: {
      title: 'IMMEDIATE',
      subtitle: 'Within 48 Hours',
      urgency: 'immediate',
      category: '',
      tasks: [],
    },

    week: {
      title: '1 WEEK',
      subtitle: 'Within 7 Days',
      urgency: 'week',
      category: '',
      tasks: [],
    },

    month: {
      title: '30 DAYS',
      subtitle: 'Within 30 Days',
      urgency: 'month',
      category: '',
      tasks: [],
    },

    months: {
      title: 'LONG TERM',
      subtitle: 'Within 90 Days or More',
      urgency: 'months',
      category: '',
      tasks: [],
    }

  };

  tasks.forEach((task) => {

    if (groupMap[task.urgency]) {

      groupMap[task.urgency].tasks.push(task);

      if (!groupMap[task.urgency].category) {

        groupMap[task.urgency].category =
          this.formatCategory(task.category);

      }

    }

  });

const grouped = Object.values(groupMap)
  .filter(group => group.tasks.length > 0);

const start =
  (this.currentPage - 1) * this.itemsPerPage;

const end = start + this.itemsPerPage;

// paginate tasks
const paginatedTasks = tasks.slice(start, end);

// clear groups
Object.keys(groupMap).forEach(key => {
  groupMap[key].tasks = [];
});

tasks.forEach(task => {

  if (groupMap[task.urgency]) {

    groupMap[task.urgency].tasks.push(task);

  }

});

this.filteredGroups = Object.values(groupMap)
  .filter(group => group.tasks.length > 0);

}
changePage(page: number): void {

  if (page < 1 || page > this.totalPages) return;

  this.currentPage = page;

  this.loadChecklist();
}


  // ── Detail panel ──────────────────────────────────────────────
  openTask(task: Task): void {
    this.selectedTask = this.selectedTask?.id === task.id ? null : task;
  }
  closeDetail(): void {
  this.selectedChecklist = null;
}

  closeDialog(): void {
    this.selectedTask = null;
  }

  editTask(): void {
    if (!this.selectedTask) return;
    const taskId = this.selectedTask.id;
    this.closeDialog();
    this.router.navigate(['/edit-task', taskId]);
  }

  deleteTask(): void {
    if (!this.selectedTask) return;
    const deletedId = this.selectedTask.id;
    this.allTasks = this.allTasks.filter((t) => t.id !== deletedId);
    this.totalTasks = this.allTasks.length;
    this.timeFilters = this.timeFilters.map((f) => ({
      ...f,
      count:
        f.value === 'all'
          ? this.allTasks.length
          : this.allTasks.filter((t) => t.timeBucket === f.value).length,
    }));
    this.closeDialog();
    this.buildFilteredGroups();
  }

  getUrgencyLabel(urgency: string | undefined): string {
    if (!urgency) return '';
    return this.urgencyLabelMap[urgency] ?? urgency;
  }

  getGroupCategory(urgency: string | undefined): string {
    if (!urgency) return '';
    return this.urgencyCategoryMap[urgency] ?? '';
  }

  addTask(): void {
    this.router.navigate(['/add-task']);
  }

  navigate(route: string): void {
    this.activeNav = route;
    this.router.navigate([`/${route}`]);
  }

  goBack(): void {
    this.router.navigate(['/beneficiary-home']);
  }
  get pendingTasksCount(): number {
  return this.allTasks.filter(t => !t.completed).length;
}
}